import "./apex25.css";

import {
  ArrowDown,
  ArrowRight,
  Compass,
  FileSearch,
  Filter,
  MessageSquareText,
  Phone,
  Users,
  Video,
} from "lucide-react";
import { useEffect } from "react";

import { APEX25_HOME_META } from "./industries";

import { demoBookingUrl } from "@/lib/apex25AppUrl";

import { ApexCompletedVisual, ApexInterviewVisual, ApexProductImage } from "./ApexProductVisuals";
import { Apex25Footer, Apex25Header, bookingLinkProps, sectionLinkProps } from "./Apex25Chrome";
import { InterviewDemo } from "./demo/InterviewDemo";

// Marketing page for tryapex25.com (and apex25.theresume.io), ported from
// apex25-homepage-v2.html. Served at "/" on those hosts and at /apex25 everywhere else (see router).

// The server sends the full <head> for "/" (apps/server/src/brand-sites); this only keeps the tab
// title right after in-app navigation.
const META = APEX25_HOME_META;

const oldSolutions = [
  { Icon: Users, title: "Delegate to a junior", body: "First-round CV review" },
  { Icon: Filter, title: "ATS auto-reject", body: "Apply hard filters" },
  { Icon: Phone, title: "Traditional Methods", body: "Phone screening or skills tests" },
  { Icon: Compass, title: "Gut feel", body: "Go with your instinct" },
];

const capabilities = [
  { Icon: FileSearch, text: "Rank CVs against other candidates for your job description." },
  { Icon: Video, text: "Invite candidates to an automated, real-time AI interview." },
  {
    Icon: MessageSquareText,
    text: "Get evidence-based feedback on each candidate's AI interview.",
  },
];

const steps = [
  {
    title: "Set Up Job Criteria",
    body: "Upload your job description or share a link to it.",
    image: "role-criteria",
  },
  {
    title: "Review and Rank CVs",
    body: "Evaluate applications against other applicants and the job criteria.",
    image: "candidate-review",
  },
  {
    title: "Invite Candidates to First Round Interview",
    body: "You choose who to invite.",
    image: "interview-invite",
  },
  {
    title: "Review Interview Feedback",
    body: "Watch a video of each interview & receive qualitative assessment.",
    image: "interview-evidence",
  },
  {
    title: "Choose who moves forward",
    body: "Your team makes the decision.",
    image: "candidate-review",
  },
] as const;

const faqs = [
  {
    question: "Who makes the ultimate hiring decision?",
    answer:
      "Your team makes the ultimate hiring decision. Apex 25 supports application review and initial screening, and your team chooses who progresses.",
  },
  {
    question: "Can we use our own hiring criteria?",
    answer: "You can define criteria for the role and evaluate CVs against those requirements.",
  },
  {
    question: "Does every applicant need to complete an AI interview?",
    answer: "No. Your team selects whom to invite.",
  },
  {
    question: "Does AI remove bias from recruitment?",
    answer:
      "No tool can guarantee a bias-free hiring process. Your team remains responsible for checking results and making decisions.",
  },
];

export const Apex25Page = () => {
  useEffect(() => {
    const previousTitle = document.title;
    const description = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = description?.content;
    document.title = META.title;
    description?.setAttribute("content", META.description);
    return () => {
      document.title = previousTitle;
      if (description && previousDescription !== undefined) {
        description.setAttribute("content", previousDescription);
      }
    };
  }, []);

  return (
    <div className="apex25" id="top">
      <Apex25Header onHome />

      <main>
        {/* The hero is kept short so the live demo starts above the fold: headline on the left,
            what it does and the actions on the right, the demo straight underneath. */}
        <section className="hero" aria-labelledby="a25-hero-title">
          <div className="wrap hero-grid">
            <div className="hero-copy">
              <div className="eyebrow hero-rise">Helping in-house HR teams around Australia</div>
              <h1 id="a25-hero-title" className="hero-rise">
                Overwhelmed by applications?
              </h1>
              <div className="lede hero-rise">
                <span>We&apos;ve got your back.</span>
              </div>
            </div>
            <div className="hero-side">
              <p className="hero-rise">
                Screen resumes, run structured interviews and rank candidates objectively, with
                humans in control of every call.
              </p>
              <div className="hero-actions hero-rise">
                <a className="btn" {...sectionLinkProps("#try-it", true)}>
                  Try the AI interview <ArrowDown size={16} aria-hidden="true" />
                </a>
                <a className="btn alt" {...bookingLinkProps("hero")}>
                  Book a demo <ArrowRight size={16} aria-hidden="true" />
                </a>
              </div>
              <p className="trust hero-rise">Free to try, about 2 minutes.</p>
            </div>
          </div>
        </section>

        <section id="try-it" className="demo-fold" aria-labelledby="a25-demo-title">
          <div className="wrap">
            <div className="demo-intro hero-rise">
              <span className="demo-live">
                <span className="demo-live-dot" aria-hidden="true" /> Live demo
              </span>
              <h2 id="a25-demo-title">
                See the AI interview from both sides: send it as the recruiter, answer as the
                candidate, then ask AI who fits with evidence from resume, interview and spotlight
                intros
                <sup className="demo-footnote-mark" aria-describedby="a25-spotlight-note">
                  *
                </sup>
                .
              </h2>
              <p id="a25-spotlight-note" className="demo-footnote">
                * Spotlight intros are optional AI-led video intros that candidates record to
                express themselves beyond their resumes.
              </p>
            </div>
            <div className="demo-frame hero-rise">
              <InterviewDemo bookingUrl={demoBookingUrl()} />
            </div>
          </div>
        </section>

        <section id="problem" className="section">
          <div className="wrap center">
            <div className="eyebrow">Your Problem</div>
            <h2>
              Too many applications,
              <br />
              too little time, not enough data.
            </h2>
            <p className="sub">
              How do you find the best candidate when multiple applications all look the same?
            </p>
            <p className="bronze section-note">The old solutions</p>
            <div className="options">
              {oldSolutions.map(({ Icon, title, body }) => (
                <div className="option" key={title}>
                  <div className="circle">
                    <Icon size={24} strokeWidth={1.6} aria-hidden="true" />
                  </div>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="solution" className="section soft">
          <div className="wrap center">
            <div className="eyebrow">Our solution</div>
            <h2>Technology that empowers your team.</h2>
            <p className="sub">Make better hiring decisions, faster.</p>
            <p className="bronze section-note">What Apex 25 does:</p>
            <div className="caps">
              {capabilities.map(({ Icon, text }) => (
                <div className="cap" key={text}>
                  <div className="check">
                    <Icon size={24} strokeWidth={1.5} aria-hidden="true" />
                  </div>
                  <p>{text}</p>
                </div>
              ))}
            </div>
            <div className="apex-product-gallery">
              <figure className="apex-product-feature">
                <div
                  className="apex-capture-frame"
                  tabIndex={0}
                  role="region"
                  aria-label="Candidate review screenshot"
                >
                  <ApexProductImage
                    name="candidate-review"
                    alt="Apex 25 demo candidate review showing a fictional candidate's resume evidence"
                  />
                </div>
                <figcaption>
                  <span>01</span> Candidate profile after ranking{" "}
                  <small>Demo interface · Fictional data</small>
                </figcaption>
              </figure>
              <div className="apex-product-pair">
                <figure>
                  <ApexInterviewVisual />
                  <figcaption>
                    <span>02</span> AI interview in progress
                  </figcaption>
                </figure>
                <figure>
                  <ApexCompletedVisual />
                  <figcaption>
                    <span>03</span> Completed interview
                  </figcaption>
                </figure>
              </div>
              <figure className="apex-product-evidence">
                <div
                  className="apex-capture-frame"
                  tabIndex={0}
                  role="region"
                  aria-label="Interview evidence screenshot"
                >
                  <ApexProductImage
                    name="interview-evidence"
                    alt="Apex 25 demo showing a fictional candidate's interview answers and timestamps"
                  />
                </div>
                <figcaption>
                  <span>04</span> Completed interview feedback{" "}
                  <small>Demo interface · Fictional data</small>
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="section">
          <div className="wrap center">
            <div className="eyebrow">How it works</div>
            <h2>Don&apos;t replace your current hiring process. Enhance it.</h2>
            <div className="steps">
              {steps.map((step, index) => (
                <article className="step" key={step.title}>
                  <div className="num">{String(index + 1).padStart(2, "0")}</div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                  <div className="apex-step-capture">
                    <ApexProductImage
                      name={step.image}
                      alt={`Demo interface: ${step.title.toLowerCase()}, using fictional data`}
                    />
                  </div>
                </article>
              ))}
            </div>
            <p className="apex-capture-disclaimer">Product views use fictional demo data.</p>
          </div>
        </section>

        <section id="faq" className="section">
          <div className="wrap center">
            <div className="eyebrow">Frequently asked questions</div>
            <h2>Before you get started.</h2>
            <div className="faq">
              {faqs.map((faq) => (
                <details key={faq.question}>
                  <summary>{faq.question}</summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Apex25Footer onHome />
    </div>
  );
};
